/**
 * 人脸服务 aegis-face（与业务 Api 响应格式不同，走 fetch）
 *
 * 地址优先：face-local.json → `/face`
 * 鉴权：业务账号只需已登录。生产/推荐路径由同域 BFF（或本地 setupProxy）注入 FACE_API_KEY；
 * face-local.json **不要**放 apiKey（装机人员也不会让门店店长去敲 ./run keys）。
 *
 * @see docs/aegis-face-API.md
 */

import Session from '@/utils/session'

const DEFAULT_BASE_URL = '/face'

/** @type {{ baseUrl: string, apiKey: string, useMockRecognition?: boolean }|null} */
let cachedLocal = null
let loadPromise = null

function normalizeBaseUrl (url) {
  const s = String(url || DEFAULT_BASE_URL).trim() || DEFAULT_BASE_URL
  return s.replace(/\/+$/, '')
}

/**
 * 读取 public/face-local.json（不存在则静默默认）
 */
export function loadFaceLocalConfig () {
  if (cachedLocal) {
    return Promise.resolve(cachedLocal)
  }
  if (loadPromise) {
    return loadPromise
  }
  loadPromise = fetch('/face-local.json', { cache: 'no-store' })
    .then((res) => (res.ok ? res.json() : {}))
    .catch(() => ({}))
    .then((data) => {
      const raw = data && typeof data === 'object' ? data : {}
      cachedLocal = {
        baseUrl: normalizeBaseUrl(raw.baseUrl || DEFAULT_BASE_URL),
        apiKey: typeof raw.apiKey === 'string' ? raw.apiKey.trim() : '',
        useMockRecognition: raw.useMockRecognition
      }
      return cachedLocal
    })
  return loadPromise
}

/**
 * @param {{ baseUrl?: string, apiKey?: string }} [override]
 */
export async function resolveFaceClientConfig (override = {}) {
  const local = await loadFaceLocalConfig()
  return {
    baseUrl: normalizeBaseUrl(override.baseUrl || local.baseUrl),
    apiKey: (override.apiKey != null ? override.apiKey : local.apiKey) || ''
  }
}

/**
 * @param {{
 *   path: string,
 *   method?: string,
 *   form?: FormData,
 *   signal?: AbortSignal,
 *   baseUrl?: string,
 *   apiKey?: string
 * }} opts
 */
async function faceFetch (opts = {}) {
  const { path, method = 'GET', form, signal } = opts
  if (!path) {
    return { ok: false, reason: 'service_error', status: 0 }
  }
  const { baseUrl, apiKey } = await resolveFaceClientConfig(opts)
  Session.ensureIkCookie()
  const headers = {}
  if (apiKey) {
    headers.Authorization = `Bearer ${apiKey}`
  }

  let res
  try {
    res = await fetch(`${baseUrl}${path}`, {
      method,
      headers,
      body: form || undefined,
      signal,
      credentials: 'same-origin'
    })
  } catch (err) {
    if (err && err.name === 'AbortError') {
      return { ok: false, reason: 'cancelled', status: 0 }
    }
    return { ok: false, reason: 'service_error', status: 0 }
  }

  if (res.status === 401) {
    // BFF：未登录 → detail=login required；直连 :4000 缺 Key → unauthorized
    let detail = ''
    try {
      const errBody = await res.clone().json()
      detail = String((errBody && (errBody.detail || errBody.reason)) || '')
    } catch (e) {
      // ignore
    }
    if (/login required/i.test(detail)) {
      return { ok: false, reason: 'login_required', status: 401, detail }
    }
    return { ok: false, reason: 'unauthorized', status: 401, detail }
  }
  if (res.status === 404) {
    return { ok: false, reason: 'not_found', status: 404 }
  }
  if (res.status === 400) {
    return { ok: false, reason: 'bad_image', status: 400 }
  }

  const contentType = String(res.headers.get('content-type') || '')
  let data = null
  try {
    data = await res.json()
  } catch (e) {
    // 常见：CRA 未加载 /face 代理时返回 index.html
    if (contentType.includes('text/html')) {
      return {
        ok: false,
        reason: 'proxy_miss',
        status: res.status,
        detail: '未代理到 aegis-face，请重启前端 npm start'
      }
    }
    return { ok: false, reason: 'service_error', status: res.status }
  }

  if (!res.ok) {
    return {
      ok: false,
      reason: (data && (data.reason || data.detail)) || 'service_error',
      status: res.status,
      data
    }
  }

  return { ok: true, status: res.status, data }
}

function normalizeFaceStoreId (storeId) {
  if (storeId === undefined || storeId === null) return ''
  return String(storeId).trim()
}

/**
 * POST /api/recognize
 * @param {{
 *   blob: Blob,
 *   storeId: string|number,
 *   frames?: Blob[],
 *   signal?: AbortSignal,
 *   baseUrl?: string,
 *   apiKey?: string
 * }} body
 */
export async function recognizeFace (body = {}) {
  const { blob, frames, signal } = body
  const storeId = normalizeFaceStoreId(body.storeId)
  if (!blob) {
    return Promise.reject(new Error('recognizeFace: blob required'))
  }
  if (!storeId) {
    return Promise.reject(new Error('recognizeFace: storeId required'))
  }
  const form = new FormData()
  form.append('storeId', storeId)
  form.append('file', blob, 'face.jpg')
  const seq = Array.isArray(frames) ? frames.filter(Boolean) : []
  seq.forEach((frame, i) => {
    form.append('files', frame, `f${i}.jpg`)
  })
  const res = await faceFetch({
    path: '/api/recognize',
    method: 'POST',
    form,
    signal,
    baseUrl: body.baseUrl,
    apiKey: body.apiKey
  })
  if (!res.ok) {
    return { ok: false, reason: res.reason || 'service_error' }
  }
  const data = res.data
  if (data && data.ok === true && data.nurseId) {
    return {
      ok: true,
      nurseId: String(data.nurseId),
      nurseName: String(data.nurseName || ''),
      confidence: data.confidence,
      liveness_score: data.liveness_score
    }
  }
  return { ok: false, reason: (data && data.reason) || 'mismatch' }
}

function withStoreIdQuery (path, storeId) {
  const sid = normalizeFaceStoreId(storeId)
  if (!sid) return path
  const sep = path.includes('?') ? '&' : '?'
  return `${path}${sep}storeId=${encodeURIComponent(sid)}`
}

/**
 * GET /api/users
 * @param {{ storeId: string|number, signal?: AbortSignal }} body
 */
export async function listFaceUsers (body = {}) {
  const storeId = normalizeFaceStoreId(body.storeId)
  if (!storeId) {
    return Promise.reject(new Error('listFaceUsers: storeId required'))
  }
  const res = await faceFetch({
    path: withStoreIdQuery('/api/users', storeId),
    method: 'GET',
    signal: body.signal
  })
  if (!res.ok) {
    return { ok: false, reason: res.reason || 'service_error', users: [] }
  }
  const data = res.data
  if (data && data.ok === true) {
    return { ok: true, users: Array.isArray(data.users) ? data.users : [] }
  }
  return { ok: false, reason: 'service_error', users: [] }
}

/**
 * POST /api/register
 * person_id 与业务员工 id（agents.id）同值；刷脸返回的 nurseId 即此 id。
 * @param {{
 *   name: string,
 *   blob: Blob,
 *   storeId: string|number,
 *   personId: string|number,
 *   append?: boolean,
 *   signal?: AbortSignal
 * }} body
 */
export async function registerFace (body = {}) {
  const name = String(body.name || '').trim()
  const storeId = normalizeFaceStoreId(body.storeId)
  const personId = body.personId != null ? String(body.personId).trim() : ''
  if (!name) {
    return Promise.reject(new Error('registerFace: name required'))
  }
  if (!body.blob) {
    return Promise.reject(new Error('registerFace: blob required'))
  }
  if (!storeId) {
    return Promise.reject(new Error('registerFace: storeId required'))
  }
  if (!personId) {
    return Promise.reject(new Error('registerFace: personId required'))
  }
  const form = new FormData()
  form.append('storeId', storeId)
  form.append('name', name)
  form.append('file', body.blob, 'enroll.jpg')
  form.append('person_id', personId)
  form.append('append', body.append ? 'true' : 'false')
  const res = await faceFetch({
    path: '/api/register',
    method: 'POST',
    form,
    signal: body.signal
  })
  if (!res.ok) {
    return { ok: false, reason: res.reason || 'service_error' }
  }
  const data = res.data
  if (data && data.ok === true && data.person_id) {
    return {
      ok: true,
      person_id: String(data.person_id),
      name: String(data.name || name),
      template_count: data.template_count
    }
  }
  return { ok: false, reason: (data && data.reason) || 'service_error' }
}

/**
 * DELETE /api/users/{person_id}
 * @param {{ personId: string|number, storeId: string|number, signal?: AbortSignal }} body
 */
export async function deleteFaceUser (body = {}) {
  const personId = String(body.personId || '').trim()
  const storeId = normalizeFaceStoreId(body.storeId)
  if (!personId) {
    return Promise.reject(new Error('deleteFaceUser: personId required'))
  }
  if (!storeId) {
    return Promise.reject(new Error('deleteFaceUser: storeId required'))
  }
  const res = await faceFetch({
    path: withStoreIdQuery(`/api/users/${encodeURIComponent(personId)}`, storeId),
    method: 'DELETE',
    signal: body.signal
  })
  if (!res.ok) {
    return { ok: false, reason: res.reason || 'service_error' }
  }
  return { ok: true, person_id: personId }
}

/** 登记/识别失败 reason → 文案 */
export const FACE_FAIL_REASON_TEXT = {
  no_face: '未检测到人脸',
  liveness_fail: '活体未通过（请正对真人，勿用照片）',
  mismatch: '未匹配到库内人员',
  quality: '画面质量不足',
  bad_image: '图片无效',
  login_required: '请先登录监控墙后再刷脸',
  unauthorized: '人脸服务鉴权失败，请联系实施检查 BFF / 服务端 FACE_API_KEY（业务账号无需配置密钥）',
  not_found: '人员不存在',
  proxy_miss: '人脸代理未生效，请重启前端（npm start）',
  service_error: '人脸服务不可用（确认 aegis-face 与 BFF 已启动）',
  cancelled: '已取消'
}
